import { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { CheckCircle2, MessageCircle } from "lucide-react";
import { useCart } from "../../context/CartContext";
import { CONTACT } from "../../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CheckoutDialog = () => {
  const { checkoutOpen, setCheckoutOpen, items, total, clear } = useCart();
  const [form, setForm] = useState({ customer_name: "", phone: "", address: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const buildWaMessage = () => {
    const lines = items.map((i) => `• ${i.name} × ${i.quantity} — ₹${i.price * i.quantity}`).join("%0a");
    return encodeURIComponent(
      `New order for Mamta's Kitchen%0a%0aName: ${form.customer_name}%0aPhone: ${form.phone}%0aAddress: ${form.address}%0a%0a`
    ).replace(/%2509/g, "") + lines + encodeURIComponent(`%0a%0aTotal: ₹${total}${form.notes ? `%0aNotes: ${form.notes}` : ""}`);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.phone || !form.address) {
      toast.error("Please fill in your name, phone and address.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/orders`, {
        ...form,
        items: items.map((i) => ({ id: i.id, name: i.name, price: i.price, quantity: i.quantity })),
        total,
      });
      const wa = `New order for Mamta's Kitchen\n\nName: ${form.customer_name}\nPhone: ${form.phone}\nAddress: ${form.address}\n\n${items
        .map((i) => `• ${i.name} x ${i.quantity} — ₹${i.price * i.quantity}`)
        .join("\n")}\n\nTotal: ₹${total}${form.notes ? `\nNotes: ${form.notes}` : ""}`;
      window.open(`https://wa.me/${CONTACT.phoneRaw}?text=${encodeURIComponent(wa)}`, "_blank");
      setDone(true);
      clear();
    } catch (err) {
      toast.error("Something went wrong placing your order. Please WhatsApp us directly.");
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    setCheckoutOpen(false);
    setTimeout(() => { setDone(false); setForm({ customer_name: "", phone: "", address: "", notes: "" }); }, 300);
  };

  return (
    <Dialog open={checkoutOpen} onOpenChange={(o) => (o ? setCheckoutOpen(true) : close())}>
      <DialogContent className="bg-[#F9F6F0] border border-[#E8E3DA] sm:max-w-lg" data-testid="checkout-dialog">
        {done ? (
          <div className="text-center py-6 px-2" data-testid="order-success">
            <div className="mx-auto h-16 w-16 rounded-full bg-[#B83A14]/10 flex items-center justify-center mb-5">
              <CheckCircle2 className="h-8 w-8 text-[#B83A14]" />
            </div>
            <h3 className="font-serif text-3xl text-[#1E1A17]">Order received!</h3>
            <p className="text-[#3D352F] mt-3 leading-relaxed">
              Thank you. We've opened WhatsApp so you can confirm with {CONTACT.owner} directly. She'll reply shortly with
              delivery details.
            </p>
            <Button onClick={close} className="mt-6 rounded-full bg-[#1E1A17] hover:bg-[#B83A14] text-white px-8 h-11" data-testid="order-success-close">
              Done
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-3xl text-[#1E1A17]">Place your order</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-[#3D352F] -mt-1">
              {items.length} item{items.length !== 1 ? "s" : ""} · Total <span className="text-[#B83A14] font-medium">₹{total}</span>
            </p>
            <form onSubmit={submit} className="space-y-4 mt-2">
              <div>
                <Label htmlFor="name" className="text-[#3D352F]">Full name</Label>
                <Input id="name" data-testid="checkout-name" value={form.customer_name} onChange={set("customer_name")} placeholder="Your name" className="mt-1.5 border-[#E8E3DA] focus-visible:ring-[#B83A14]" />
              </div>
              <div>
                <Label htmlFor="phone" className="text-[#3D352F]">Phone number</Label>
                <Input id="phone" data-testid="checkout-phone" value={form.phone} onChange={set("phone")} placeholder="+91 " className="mt-1.5 border-[#E8E3DA] focus-visible:ring-[#B83A14]" />
              </div>
              <div>
                <Label htmlFor="address" className="text-[#3D352F]">Delivery address</Label>
                <Textarea id="address" data-testid="checkout-address" value={form.address} onChange={set("address")} placeholder="House / flat, society, landmark, Delhi NCR" className="mt-1.5 border-[#E8E3DA] focus-visible:ring-[#B83A14]" rows={2} />
              </div>
              <div>
                <Label htmlFor="notes" className="text-[#3D352F]">Notes <span className="text-[#B4A79A]">(optional)</span></Label>
                <Textarea id="notes" data-testid="checkout-notes" value={form.notes} onChange={set("notes")} placeholder="Spice preference, gift message…" className="mt-1.5 border-[#E8E3DA] focus-visible:ring-[#B83A14]" rows={2} />
              </div>
              <div className="flex items-start gap-2.5 rounded-md bg-[#F2EBE1] border border-[#E8E3DA] px-3.5 py-3" data-testid="checkout-whatsapp-note">
                <MessageCircle className="h-4 w-4 text-[#25D366] mt-0.5 shrink-0" />
                <p className="text-xs text-[#3D352F] leading-relaxed">
                  All orders are placed &amp; confirmed on <span className="font-medium">WhatsApp only</span>. On submit we'll open a chat with your order details for {CONTACT.owner} to confirm.
                </p>
              </div>
              <Button type="submit" disabled={loading || items.length === 0} data-testid="checkout-submit" className="w-full rounded-full bg-[#B83A14] hover:bg-[#942B0D] text-white h-12 text-sm tracking-wide">
                {loading ? "Placing order…" : "Place order & confirm on WhatsApp"}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
